import { useEffect, useState } from 'react';
import {
    Text,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Button,
    Checkbox,
    Stack,
    useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'helpers/languages/translations';
import { useDeleteCustomColumnsMutation } from 'core/API/productsApi';

type TUpdateModal = { isOpen: boolean; onClose: () => void; customFields?: string[] };

const DeleteCustomColumnsModal = ({ isOpen, onClose, customFields }: TUpdateModal) => {
    const translate = useTranslation();
    const toast = useToast();

    const [deleteCustomColumns, { isLoading }] = useDeleteCustomColumnsMutation();

    const [checked, setChecked] = useState<Record<string, boolean>>({});

    const onCheck = (val: string) => {
        setChecked((check) => {
            const newCheck = { ...check };

            if (newCheck[val]) {
                delete newCheck[val];
            } else {
                newCheck[val] = true;
            }

            return newCheck;
        });
    };

    const handleDeleteCustomColumn = async () => {
        const resp = await deleteCustomColumns({ customColumns: Object.keys(checked) });
        const data = (resp as any).data || (resp as any).error?.data;

        toast({
            title: translate(`product.deleteCustomColumn.${data.code === 200 ? 'success' : 'error'}`),
            status: data.code === 200 ? 'success' : 'error',
        });

        setChecked({});
        onClose();
    };

    return (
        <Modal isCentered isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text as="h3">{translate('product.deleteCustomColumn.header')}</Text>
                </ModalHeader>
                <ModalCloseButton mt="2" disabled={isLoading} />
                <ModalBody>
                    <Text fontSize="sm" mb={3}>
                        {translate('product.deleteCustomColumn.confirmation')}
                    </Text>
                    <Stack>
                        {customFields?.map((cf) => (
                            <Checkbox isChecked={!!checked[cf]} onChange={() => onCheck(cf)}>
                                {cf}
                            </Checkbox>
                        ))}
                    </Stack>
                </ModalBody>

                <ModalFooter>
                    <Button onClick={onClose} isDisabled={isLoading} variant="outline" size="sm" mr="3">
                        {translate('product.deleteCustomColumn.cancel')}
                    </Button>
                    <Button onClick={handleDeleteCustomColumn} isLoading={isLoading} colorScheme="red" size="sm">
                        {translate('product.deleteCustomColumn.apply')}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default DeleteCustomColumnsModal;
